import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BankTransferDialog } from "@/components/BankTransferDialog";
import { TourPlayer } from "@/components/TourPlayer";
import { GuideShelf } from "@/components/GuideShelf";
import { CreditCard, Crown, Heart, Landmark, Loader2, ShieldCheck, Sparkles, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PracticeCard } from "@/components/PracticeCard";

const GOLD = ["#F2B733", "#C6CBD4", "#C98B57"];

export default function Support() {
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [limits, setLimits] = useState({ min: 1, max: 1000 });
  const [stats, setStats] = useState(null);
  const [tierId, setTierId] = useState("supporter");
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState({ enabled: false });
  const [cards, setCards] = useState({ enabled: false });
  const [momo, setMomo] = useState({ enabled: false });
  const [method, setMethod] = useState("card");
  const [intent, setIntent] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [wall, setWall] = useState([]);
  const [top, setTop] = useState([]);

  const loadWall = () =>
    api
      .get("/donations/wall")
      .then(({ data }) => {
        setWall(data.recent || []);
        setTop(data.top || []);
      })
      .catch(() => {});

  useEffect(() => {
    api.get("/donations/tiers").then(({ data }) => {
      setTiers(data.tiers);
      setLimits({ min: data.min, max: data.max });
    });
    api.get("/donations/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/localpay/config").then(({ data }) => setLocal(data)).catch(() => {});
    api.get("/lemonsqueezy/config").then(({ data }) => setCards(data)).catch(() => {});
    api.get("/momo/config").then(({ data }) => setMomo(data)).catch(() => {});
    loadWall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const amountBody = () => {
    const customAmount = custom.trim() === "" ? null : Number(custom);
    if (customAmount !== null && (Number.isNaN(customAmount) || customAmount < limits.min || customAmount > limits.max)) {
      toast.error(`Enter an amount between $${limits.min} and $${limits.max}`);
      return null;
    }
    return customAmount !== null ? { custom_amount: customAmount } : { tier_id: tierId };
  };

  const donate = async () => {
    const amount = amountBody();
    if (!amount) return;
    setBusy(true);
    try {
      if (method === "bank") {
        const { data } = await api.post("/localpay/intent", {
          kind: "donation",
          ...amount,
          milu_session: localStorage.getItem("chatmilu.milu.session") || undefined,
        });
        setIntent({ ...data, payer_default: name, note, public: isPublic });
        setBusy(false);
        return;
      }
      const shared = {
        origin_url: window.location.origin,
        ...amount,
        display_name: name || undefined,
        note: note || undefined,
        public: isPublic,
        milu_session: localStorage.getItem("chatmilu.milu.session") || undefined,
      };
      if (method === "momo") {
        const { data } = await api.post("/momo/create", { kind: "donation", ...shared });
        window.location.href = data.pay_url;
        return;
      }
      const path = cards.enabled ? "/lemonsqueezy/checkout" : "/payments/donate";
      const { data } = await api.post(path, cards.enabled ? { kind: "donation", ...shared } : shared);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const active = custom.trim() !== "" ? null : tierId;
  const methods = [
    { id: "card", label: "Card", icon: CreditCard },
    ...(local.enabled ? [{ id: "bank", label: "Bank transfer", icon: Landmark }] : []),
    ...(momo.enabled ? [{ id: "momo", label: "MoMo", icon: Wallet }] : []),
  ];
  const customNum = Number(custom);
  const customValid =
    custom.trim() !== "" && !Number.isNaN(customNum) && customNum >= limits.min && customNum <= limits.max;

  return (
    <div className="mx-auto max-w-5xl px-5 py-16 sm:px-8" data-testid="support-page">
      <p className="text-xs font-semibold uppercase tracking-widest text-brand">Support</p>
      <h1 className="mt-3 font-display text-[1.8rem] font-extrabold tracking-tighter sm:text-[2.4rem]">
        Let's Keep ChatMiLu Speaking
      </h1>
      <p className="mt-3 max-w-xl text-sm text-ink-soft md:text-base">
        Every translated message costs us a fraction of a cent. Donations keep the free tier free for the people
        who need it most — no account required, one-time, cancel-nothing.
      </p>

      {user && <PracticeCard />}

      <div className="mt-10" data-testid="support-tour">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
          New here? Take the tour
        </p>        <div className="mt-3 max-w-2xl">
          <TourPlayer />
        </div>

        <div className="mt-8">
          <GuideShelf />
        </div>
      </div>

      {stats && (
        <div className="mt-8 flex flex-wrap gap-3" data-testid="support-stats">
          <span className="rounded-full border border-forest/30 bg-forest/10 px-4 py-2 text-sm">
            <b data-testid="support-total">${stats.total.toFixed(2)}</b> raised so far
          </span>
          <span className="rounded-full border border-white/10 bg-panel px-4 py-2 text-sm text-ink-soft">
            <b data-testid="support-count">{stats.supporters}</b> supporters
          </span>
        </div>
      )}

      <div className="mt-12 grid gap-8 lg:grid-cols-[1.1fr_1fr]">
        <div className="grid gap-3 sm:grid-cols-2" data-testid="support-tiers">
          {tiers.map((t, i) => (
            <button
              key={t.id}
              data-testid={`donate-tier-${t.id}`}
              onClick={() => {
                setTierId(t.id);
                setCustom("");
              }}
              style={{ animationDelay: `${i * 60}ms` }}
              className={`rise rounded-3xl border p-6 text-start transition-all duration-300 hover:-translate-y-0.5 ${
                active === t.id
                  ? "border-brand bg-brand/10 shadow-[0_0_28px_rgba(26,125,255,0.18)]"
                  : "border-white/10 bg-panel hover:border-white/25"
              }`}
            >
              <p className="font-display text-3xl font-extrabold tracking-tighter">${t.amount}</p>
              <p className="mt-1 text-sm font-semibold">{t.label}</p>
              <p className="mt-1 text-xs leading-relaxed text-ink-soft">{t.blurb}</p>
            </button>
          ))}
        </div>

        <div className="rounded-3xl border border-white/10 bg-panel p-7">
          {methods.length > 1 && (
            <div
              className="mb-6 grid gap-2 rounded-full bg-white/5 p-1"
              style={{ gridTemplateColumns: `repeat(${methods.length}, minmax(0, 1fr))` }}
              data-testid="donate-method"
            >
              {methods.map((m) => (
                <button
                  key={m.id}
                  data-testid={`donate-method-${m.id}`}
                  onClick={() => setMethod(m.id)}
                  className={`flex items-center justify-center gap-2 rounded-full px-3 py-2.5 text-sm font-medium transition-colors ${
                    method === m.id ? "bg-brand text-white" : "text-ink-soft hover:text-ink"
                  }`}
                >
                  <m.icon className="h-4 w-4" /> {m.label}
                </button>
              ))}
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-widest text-ink-soft" htmlFor="donate-custom">
              Or your own amount (USD)
            </label>
            <Input
              id="donate-custom"
              data-testid="donate-custom-input"
              type="number"
              min={limits.min}
              max={limits.max}
              placeholder={`${limits.min} – ${limits.max}`}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              className="mt-2 min-h-[48px] rounded-xl"
            />
          </div>

          <div className="mt-5">
            <label className="text-xs uppercase tracking-widest text-ink-soft" htmlFor="donate-name">
              Name to thank (optional)
            </label>
            <Input
              id="donate-name"
              data-testid="donate-name-input"
              maxLength={60}
              placeholder="Anonymous"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 min-h-[44px] rounded-xl"
            />
          </div>

          <div className="mt-5">
            <label className="text-xs uppercase tracking-widest text-ink-soft" htmlFor="donate-note">
              Leave a note (optional)
            </label>
            <Textarea
              id="donate-note"
              data-testid="donate-note-input"
              maxLength={280}
              rows={3}
              placeholder="Which language should we add next?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mt-2 rounded-xl"
            />
          </div>

          <label className="mt-5 flex cursor-pointer items-start gap-2.5 text-sm">
            <input
              data-testid="donate-public"
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#1A7DFF]"
            />
            <span className="text-ink-soft">
              Show my name and note on the supporter wall below
            </span>
          </label>

          <Button
            data-testid="donate-submit"
            onClick={donate}
            disabled={busy}
            className="mt-7 min-h-[52px] w-full rounded-full bg-brand text-base hover:bg-brand-hover active:scale-95"
          >
            {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Heart className="me-2 h-4 w-4" />}
            {custom.trim() !== ""
              ? customValid
                ? `Donate $${customNum}`
                : `Enter $${limits.min}–$${limits.max}`
              : `Donate $${tiers.find((t) => t.id === tierId)?.amount ?? ""}`}
          </Button>

          <p className="mt-4 flex items-start gap-2 text-xs text-ink-soft">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-forest" />
            {method === "bank"
              ? `Scan the VietQR with any Vietnamese bank app — the money goes straight to ${
                  local.bank_name || "our bank"
                }, no card needed.`
              : method === "momo"
              ? "You'll finish in the MoMo app or with a MoMo QR — nothing to type in."
              : cards.enabled
              ? "Cards are handled by Lemon Squeezy, our merchant of record — we never see your card."
              : "Payment is handled by Stripe Checkout — we never see your card. Donations are one-time and never change your plan."}
          </p>
        </div>
      </div>

      {(top.length > 0 || wall.length > 0) && (
        <section className="mt-16" data-testid="supporter-wall">
          {top.length > 0 && (
            <div className="mb-12" data-testid="top-supporters">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight md:text-lg">
                <Crown className="h-5 w-5" style={{ color: GOLD[0] }} /> Top supporters
              </h2>
              <p className="mt-1 text-sm text-ink-soft">
                The biggest hearts, all time. Give more than the leader and this is your seat.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {top.map((t, i) => (
                  <div
                    key={t.name}
                    data-testid={`top-supporter-${t.rank}`}
                    style={{
                      animationDelay: `${i * 80}ms`,
                      borderColor: `${GOLD[i]}55`,
                      background: `linear-gradient(160deg, ${GOLD[i]}1f, transparent 70%)`,
                    }}
                    className="rise rounded-3xl border p-6"
                  >
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4" style={{ color: GOLD[i] }} />
                      <span
                        className="text-xs font-bold uppercase tracking-widest"
                        style={{ color: GOLD[i] }}
                      >
                        #{t.rank}
                      </span>
                    </div>
                    <p className="mt-3 truncate font-display text-xl font-extrabold tracking-tighter">
                      {t.name}
                    </p>
                    <p className="mt-1 text-sm text-ink-soft">
                      ${t.total.toFixed(2)} · {t.gifts} {t.gifts === 1 ? "gift" : "gifts"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <h2 className="font-display text-lg font-bold tracking-tight md:text-lg">
            The people keeping this alive
          </h2>
          <p className="mt-1 text-sm text-ink-soft">
            Everyone below chose to be named. Thank you, all of you.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {wall.map((s, i) => (
              <div
                key={`${s.name}-${s.created_at}-${i}`}
                data-testid={`supporter-${i}`}
                style={{
                  animationDelay: `${Math.min(i, 8) * 50}ms`,
                  ...(s.rank ? { borderColor: `${GOLD[s.rank - 1]}55` } : {}),
                }}
                className="rise rounded-2xl border border-white/10 bg-panel p-5"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <p className="truncate font-semibold">{s.name}</p>
                  <span className="shrink-0 rounded-full bg-brand/15 px-2.5 py-1 text-xs font-semibold text-brand">
                    ${s.amount}
                  </span>
                </div>
                {s.rank && (
                  <span
                    data-testid={`supporter-badge-${i}`}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                    style={{ color: GOLD[s.rank - 1], background: `${GOLD[s.rank - 1]}1f` }}
                  >
                    <Crown className="h-3 w-3" /> Top supporter #{s.rank}
                  </span>
                )}
                {s.note && <p className="mt-2 text-sm leading-relaxed text-ink-soft">“{s.note}”</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="mt-14 rounded-3xl border border-white/10 bg-panel p-7">
        <p className="font-display text-lg font-bold tracking-tight">Want features instead of karma?</p>
        <p className="mt-1 text-sm text-ink-soft">
          Paid plans unlock unlimited AI messages, priority models, group rooms and analytics.
        </p>
        <Link to="/pricing">
          <Button
            data-testid="support-goto-pricing"
            variant="outline"
            className="mt-5 min-h-[44px] rounded-full px-6"
          >
            <Sparkles className="me-2 h-4 w-4" /> See plans
          </Button>
        </Link>
      </div>

      <p className="mt-8 text-xs text-ink-soft">
        {cards.enabled
          ? "Card payments are live through Lemon Squeezy."
          : "Card payments are in Stripe test mode — card 4242 4242 4242 4242, any future expiry, any CVC."}
      </p>

      {intent && (
        <BankTransferDialog
          intent={intent}
          onClose={() => setIntent(null)}
          onApproved={() => {
            api.get("/donations/stats").then(({ data }) => setStats(data)).catch(() => {});
            loadWall();
          }}
        />
      )}
    </div>
  );
}
